import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { FetchResult, useLazyQuery, useMutation } from '@apollo/client';
import { connect } from 'react-redux';
import { editingItemQuery } from '../../../../api/queries';
import { getLockedAlert } from '../../../../utils/helpers/itemEdit';
import { buttonStyles, ManagementSectionContainer } from './styled';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import { InformationPopup } from '../../../ui/InformationPopup';
import useScreenSize from '../../../../hooks/useScreenSize';
import { deleteItemMutation } from '../../../../api/mutations';
import Bugsnag from '../../../../utils/bugsnag';
import { ItemStatusEnum } from '../../../../graphql/types';
import {
  DeleteItemMutation,
  DeleteItemMutationVariables,
  ItemUpdateStatusEnum
} from '../../../../graphql/graphql';
import { setError } from '../../../../actions';

interface ManagementSectionProps {
  itemPath: string;
  isRemoveAvailable: boolean;
  isPublished: boolean;
  isEditAvailable: boolean;
  setErrorUI: (value: boolean) => void;
}

const mapDispatchToProps = dispatch => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

export const ManagementSectionComponent: React.FC<ManagementSectionProps> = ({
  itemPath,
  isEditAvailable,
  isRemoveAvailable,
  setErrorUI,
  isPublished
}) => {
  const router = useRouter();
  const { alert } = router.query;
  const alertParam = Array.isArray(alert) ? alert[0] : alert;
  const { isSmall } = useScreenSize();
  const { t } = useTranslation();
  const { id } = router.query;
  const initialEditAlert = { show: false, title: '', button: '', info: '' };

  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [showRemoveLoading, setShowRemoveLoading] = useState(false);
  const [editAlert, setEditAlert] = useState(initialEditAlert);

  const [isLockedError, setIsLockedError] = useState(false);

  const [deleteItem, { loading: isItemDeleting }] =
    useMutation(deleteItemMutation);

  const [fetchItem] = useLazyQuery(editingItemQuery, {
    variables: { itemId: id },
    fetchPolicy: 'network-only'
  });

  const defaultAlert = {
    button: t('pages.current_item.errors.button'),
    info: t('pages.current_item.errors.message'),
    title: t('pages.current_item.errors.title')
  };

  const onOpenEditPage = async () => {
    try {
      const { data, errors, error } = await fetchItem();
      if (data?.itemEdit) router.push(`${itemPath}/edit`);
      if (error) {
        const e = errors && errors[0]?.extensions;
        const errorAlert =
          e?.code === 'LOCKED' ? getLockedAlert(e.until, t) : defaultAlert;
        setIsLockedError(e?.code === 'LOCKED');
        setEditAlert({ ...errorAlert, show: true });
      }
    } catch (e) {
      console.error('There was an error!', e);
    }
  };

  useEffect(() => {
    if (!router.isReady) return;
    if (alertParam === ItemUpdateStatusEnum.Locked.toLowerCase()) {
      onOpenEditPage();
    }
  }, [router.isReady, alertParam]);

  const closeEditAlertHandler = () => {
    setEditAlert(initialEditAlert);

    if (isLockedError) {
      const { alert: _, ...rest } = router.query;
      router.replace({ pathname: router.pathname, query: rest }, undefined, {
        shallow: true
      });
    } else {
      window.location.reload();
    }
  };

  const showRemoveAlertHandler = () => setShowRemoveAlert(true);
  const hideRemoveAlertHandler = () => setShowRemoveAlert(false);

  const onFailedRemoveReq = error => {
    setErrorUI(true);
    Bugsnag.notify(error);
    setShowRemoveLoading(false);
    hideRemoveAlertHandler();
  };

  const onRemoveHandler = () => {
    if (isItemDeleting) return false;
    setShowRemoveLoading(true);
    deleteItem({ variables: { itemId: id } as DeleteItemMutationVariables })
      .then((data: FetchResult<DeleteItemMutation>) => {
        if (data.data?.deleteItem?.item?.state === ItemStatusEnum.Expired) {
          router.reload();
        } else if (data.data?.deleteItem?.errors)
          onFailedRemoveReq(data.data?.deleteItem?.errors);
      })
      .catch(onFailedRemoveReq);
  };

  const buttonsArray = [
    {
      name: t('pages.current_item.buttons.edit'),
      state: ButtonType.edit,
      isShow: isEditAvailable,
      cypress: 'management-section__btn--edit',
      onClickHandler: onOpenEditPage
    },
    {
      onClickHandler: showRemoveAlertHandler,
      name: t('pages.current_item.buttons.remove'),
      state: ButtonType.delete,
      cypress: 'management-section__btn--remove',
      isShow: isRemoveAvailable
    }
  ];

  return (
    (isPublished || showRemoveLoading) && (
      <>
        <ManagementSectionContainer data-cy="management-section">
          {buttonsArray.map(
            ({ name, onClickHandler, isShow, state, cypress }) => {
              if (!isShow) return null;

              return (
                <Button
                  key={name}
                  text={name}
                  onClick={onClickHandler}
                  state={state}
                  styles={buttonStyles}
                  cypress={cypress}
                />
              );
            }
          )}
        </ManagementSectionContainer>

        <InformationPopup
          isOpen={showRemoveAlert}
          modalId="current-item-remove-popup"
          title={t('pages.current_item.alert_modal.remove_ad')}
          message={t('pages.current_item.alert_modal.remove_ad_desc')}
          declineButtonText={t('common.cancel')}
          acceptButtonText={t('pages.current_item.buttons.remove')}
          onDeclineHandlerClick={hideRemoveAlertHandler}
          onAcceptHandlerClick={onRemoveHandler}
          reverseButton={false}
          loading={showRemoveLoading}
        />

        <InformationPopup
          isOpen={editAlert.show}
          modalId="current-item-alert-popup"
          title={editAlert.title}
          message={editAlert.info}
          declineButtonText={editAlert.button}
          titleTag={isSmall ? 'h5_new' : 'header_bold'}
          onDeclineHandlerClick={closeEditAlertHandler}
        />
      </>
    )
  );
};

export const ManagementSection = connect(
  null,
  mapDispatchToProps
)(ManagementSectionComponent);
